import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isString from 'lodash/isString';
import template from 'lodash/template';
import update from 'lodash/update';
import T from 'prop-types';
import React, { useContext, useState } from 'react';

const LocaleContext = React.createContext(null);
const TextScopeContext = React.createContext({ scope: [] });
const OptionalContext = React.createContext({ isOptional: false });

const mkPath = (scope, path) => [
  ...(typeof scope === 'string' ? scope.split('.') : scope),
  ...(typeof path === 'string' ? path.split('.') : path),
];

export const Locale = ({ culture, translations, ...rest }) => (
  <LocaleContext.Provider value={{ translations, culture }} {...rest} />
);

Locale.propTypes = {
  translations: T.object,
  culture: T.object,
};

const resolvePath = (scope, path) =>
  path && mkPath(scope, path === '.' ? [] : path);

const getTranslation = (translations, paths) =>
  paths.reduce((acc, path) => {
    if (path && (acc === null || acc.value === null)) {
      if (path === '.') {
        return {
          path: 'root',
          value: translations,
        };
      }

      return {
        path,
        value: get(translations, path, null),
      };
    }

    return acc;
  }, null);

export const Translation = ({
  absolutePath,
  children,
  fallbackAbsolutePath,
  fallbackPath,
  path,
}) => {
  const { translations } = useContext(LocaleContext);
  const { scope } = useContext(TextScopeContext);
  const { isOptional, setDisplayChildren } = useContext(OptionalContext);

  const translation = getTranslation(translations, [
    absolutePath,
    resolvePath(scope, path),
    fallbackAbsolutePath,
    resolvePath(scope, fallbackPath),
  ]);

  if (!translation || !translation.value) {
    if (!isOptional) {
      const keyPath = absolutePath || resolvePath(scope, path);
      throw new Error(`MissingTranslation: {${[].concat(keyPath).join('.')}}`);
    } else {
      setDisplayChildren(false);
    }
  }

  if (!translation || !translation.value) {
    return null;
  }

  if (typeof children === 'function') {
    return children(translation.value, translation.path);
  }

  if (typeof translation.value === 'string') {
    return translation.value;
  }
};

Translation.propTypes = {
  fallbackPath: T.string,
  fallbackAbsolutePath: T.string,
  path: T.oneOfType([T.string, T.arrayOf(T.string)]),
  absolutePath: T.string,
  children: T.oneOfType([T.func, T.node]),
};

export const Translations = ({ children }) => {
  if (typeof children !== 'function')
    throw new Error('<Translations /> requires a render prop.');

  return <LocaleContext.Consumer>{children}</LocaleContext.Consumer>;
};

Translations.propTypes = {
  children: T.func.isRequired,
};

export const TranslationScope = ({ path, ...rest }) => (
  <TextScopeContext.Provider value={{ scope: path }} {...rest} />
);

TranslationScope.propTypes = {
  path: T.oneOfType([T.string, T.arrayOf(T.string)]),
};

export const Optional = props => {
  const [displayChildren, setDisplayChildren] = useState(true);
  return (
    <>
      {displayChildren ? (
        <OptionalContext.Provider
          value={{ isOptional: true, setDisplayChildren }}
          {...props}
        />
      ) : null}
    </>
  );
};

export const useTranslations = () => {
  const locale = useContext(LocaleContext);
  return locale && locale.translations ? locale.translations : {};
};

export const useCultureConfiguration = () => {
  const locale = useContext(LocaleContext);
  return locale && locale.culture ? locale.culture : {};
};

export const interpolateTranslationNode = (translationNode, interpolations) => {
  const interpolated = cloneDeep(translationNode);

  for (const [interpolationPath, interpolationData] of interpolations) {
    const pathContent = get(interpolated, interpolationPath);

    if (!pathContent || !isString(pathContent)) {
      console.warn(
        `Cannot interpolate, must be a valid path. Given path: ${interpolationPath}`
      );

      continue;
    }

    update(interpolated, interpolationPath, node => {
      try {
        return template(node)(interpolationData);
      } catch {
        console.warn(
          `Cannot interpolate, incorrect data provided. Given path: ${interpolationPath}, data: ${JSON.stringify(
            interpolationData
          )}`
        );

        return pathContent;
      }
    });
  }

  return interpolated;
};
